import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { LOGO_URL } from "@/lib/constants";
import { toast } from "sonner";
import QRReviewCard from "@/components/QRReviewCard";
import GbpPanel from "@/components/admin/GbpPanel";

const RESOURCES = [
  { key: "rooms", label: "Rooms", endpoint: "/rooms", fields: [
    { name: "name", label: "Name", type: "text", required: true },
    { name: "slug", label: "Slug", type: "text", required: true },
    { name: "bed_config", label: "Bed configuration", type: "text", required: true },
    { name: "max_guests", label: "Max guests", type: "number", required: true, default: 2 },
    { name: "size_sqft", label: "Size (sq ft)", type: "number" },
    { name: "price_from", label: "Price from ($)", type: "number" },
    { name: "image_url", label: "Hero image URL", type: "text", required: true },
    { name: "description", label: "Description", type: "textarea", required: true },
    { name: "features", label: "Features (comma-separated)", type: "csv" },
    { name: "gallery", label: "Gallery image URLs (comma-separated)", type: "csv" },
    { name: "ada_accessible", label: "ADA accessible", type: "bool" },
    { name: "sort_order", label: "Sort order", type: "number", default: 0 },
  ]},
  { key: "amenities", label: "Amenities", endpoint: "/amenities", fields: [
    { name: "title", label: "Title", type: "text", required: true },
    { name: "description", label: "Description", type: "textarea", required: true },
    { name: "icon", label: "Icon key", type: "text", default: "sparkle", help: "coffee, wifi, paw, truck, phone, fridge, bed, clock, sparkle" },
    { name: "sort_order", label: "Sort order", type: "number", default: 0 },
  ]},
  { key: "testimonials", label: "Testimonials", endpoint: "/testimonials", fields: [
    { name: "quote", label: "Quote", type: "textarea", required: true },
    { name: "author", label: "Author", type: "text", required: true },
    { name: "source", label: "Source", type: "text", default: "Guest Review" },
    { name: "rating", label: "Rating (1-5)", type: "number", default: 5 },
    { name: "sort_order", label: "Sort order", type: "number", default: 0 },
  ]},
  { key: "attractions", label: "Local Guide", endpoint: "/attractions", fields: [
    { name: "name", label: "Name", type: "text", required: true },
    { name: "description", label: "Description", type: "textarea", required: true },
    { name: "category", label: "Category", type: "text", default: "Nature" },
    { name: "distance", label: "Distance", type: "text" },
    { name: "image_url", label: "Image URL", type: "text" },
    { name: "sort_order", label: "Sort order", type: "number", default: 0 },
  ]},
  { key: "blog", label: "Blog", endpoint: "/blog", fields: [
    { name: "title", label: "Title", type: "text", required: true },
    { name: "slug", label: "Slug", type: "text", required: true },
    { name: "excerpt", label: "Excerpt (150–200 char)", type: "textarea", required: true },
    { name: "meta_description", label: "Meta description (max 160 char)", type: "textarea" },
    { name: "category", label: "Category", type: "text" },
    { name: "city", label: "Target city / keyword cluster", type: "text" },
    { name: "keywords", label: "SEO keywords (comma-separated)", type: "csv" },
    { name: "hero_image", label: "Hero image URL", type: "text" },
    { name: "body", label: "Body (markdown — use ## H2, ** bold, - list, [text](url))", type: "textarea", required: true },
    { name: "author", label: "Author", type: "text", default: "Westbridge Front Desk" },
    { name: "sort_order", label: "Sort order", type: "number", default: 0 },
  ]},
  { key: "faqs", label: "FAQ", endpoint: "/faqs", fields: [
    { name: "question", label: "Question", type: "text", required: true },
    { name: "answer", label: "Answer", type: "textarea", required: true },
    { name: "category", label: "Category", type: "text", default: "General" },
    { name: "sort_order", label: "Sort order", type: "number", default: 0 },
  ]},
  { key: "reviews", label: "Reviews", endpoint: "/reviews", fields: [
    { name: "author", label: "Author name", type: "text", required: true },
    { name: "rating", label: "Rating (1-5)", type: "number", default: 5 },
    { name: "text", label: "Review text", type: "textarea", required: true },
    { name: "source", label: "Source", type: "text", default: "Google" },
    { name: "source_url", label: "Source URL", type: "text" },
    { name: "avatar_url", label: "Avatar URL (optional)", type: "text" },
    { name: "sort_order", label: "Sort order", type: "number", default: 0 },
  ]},
];

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("rooms");
  const [inquiries, setInquiries] = useState([]);

  useEffect(() => {
    api.get("/inquiries").then((r) => setInquiries(r.data)).catch(() => {});
  }, [tab]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-canvas" data-testid="admin-page">
      <header className="sticky top-0 z-40 bg-canvas/90 backdrop-blur border-b border-rule-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="w-9 h-9 rounded-sm" />
            <div>
              <p className="font-display text-xl text-ink leading-none">Westbridge · Admin</p>
              <p className="font-mono-label text-ink-muted mt-0.5">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="font-mono-label text-ink-soft hover:text-ember" data-testid="admin-view-site">View site →</a>
            <Button variant="outline" onClick={handleLogout} data-testid="admin-logout" className="rounded-none">Sign out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-sand rounded-none p-1 h-auto flex-wrap gap-1">
            {RESOURCES.map((r) => (
              <TabsTrigger key={r.key} value={r.key} className="rounded-none px-5 py-2 data-[state=active]:bg-ink data-[state=active]:text-canvas font-mono-label" data-testid={`tab-${r.key}`}>
                {r.label}
              </TabsTrigger>
            ))}
            <TabsTrigger value="analytics" className="rounded-none px-5 py-2 data-[state=active]:bg-ink data-[state=active]:text-canvas font-mono-label" data-testid="tab-analytics">
              Analytics
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-none px-5 py-2 data-[state=active]:bg-ink data-[state=active]:text-canvas font-mono-label" data-testid="tab-settings">
              Local SEO
            </TabsTrigger>
            <TabsTrigger value="gbp" className="rounded-none px-5 py-2 data-[state=active]:bg-ink data-[state=active]:text-canvas font-mono-label" data-testid="tab-gbp">
              GBP Posts
            </TabsTrigger>
            <TabsTrigger value="qrcard" className="rounded-none px-5 py-2 data-[state=active]:bg-ink data-[state=active]:text-canvas font-mono-label" data-testid="tab-qrcard">
              Review QR Card
            </TabsTrigger>
            <TabsTrigger value="inquiries" className="rounded-none px-5 py-2 data-[state=active]:bg-ink data-[state=active]:text-canvas font-mono-label" data-testid="tab-inquiries">
              Inquiries
              {inquiries.length > 0 && <span className="ml-2 bg-ember text-canvas px-2 py-0.5 text-[10px]">{inquiries.length}</span>}
            </TabsTrigger>
          </TabsList>

          {RESOURCES.map((r) => (
            <TabsContent key={r.key} value={r.key} className="mt-8">
              <ResourceManager resource={r} />
            </TabsContent>
          ))}

          <TabsContent value="analytics" className="mt-8">
            <Analytics />
          </TabsContent>

          <TabsContent value="settings" className="mt-8">
            <SettingsPanel />
          </TabsContent>

          <TabsContent value="gbp" className="mt-8">
            <GbpPanel />
          </TabsContent>

          <TabsContent value="qrcard" className="mt-8">
            <QRReviewCard />
          </TabsContent>

          <TabsContent value="inquiries" className="mt-8">
            <Inquiries inquiries={inquiries} reload={() => api.get("/inquiries").then((r) => setInquiries(r.data))} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function ResourceManager({ resource }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(resource.endpoint);
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [resource.key]); // eslint-disable-line

  const openNew = () => {
    const init = {};
    resource.fields.forEach((f) => {
      if (f.type === "csv") init[f.name] = "";
      else if (f.type === "bool") init[f.name] = false;
      else if (f.type === "number") init[f.name] = f.default ?? "";
      else init[f.name] = f.default ?? "";
    });
    setEditing({ __new: true, data: init });
  };

  const openEdit = (item) => {
    const data = { ...item };
    resource.fields.forEach((f) => {
      if (f.type === "csv") data[f.name] = (item[f.name] || []).join(", ");
    });
    setEditing({ __new: false, data, id: item.id });
  };

  const save = async () => {
    const payload = {};
    resource.fields.forEach((f) => {
      let v = editing.data[f.name];
      if (f.type === "csv") {
        payload[f.name] = (v || "").split(",").map((s) => s.trim()).filter(Boolean);
      } else if (f.type === "number") {
        payload[f.name] = v === "" || v === null ? null : Number(v);
      } else if (f.type === "bool") {
        payload[f.name] = !!v;
      } else {
        payload[f.name] = v ?? "";
      }
    });
    try {
      if (editing.__new) {
        await api.post(resource.endpoint, payload);
        toast.success("Created");
      } else {
        await api.put(`${resource.endpoint}/${editing.id}`, payload);
        toast.success("Saved");
      }
      setEditing(null);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    }
  };

  const del = async (item) => {
    if (!window.confirm(`Delete "${item.name || item.title || item.author}"?`)) return;
    await api.delete(`${resource.endpoint}/${item.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-3xl text-ink">{resource.label}</h2>
          <p className="font-mono-label text-ink-muted mt-1">{items.length} item{items.length !== 1 ? "s" : ""}</p>
        </div>
        <Button onClick={openNew} className="btn-ember !rounded-none" data-testid={`new-${resource.key}-btn`}>+ New {resource.label.replace(/s$/, "").toLowerCase()}</Button>
      </div>

      <div className="border border-rule-strong divide-y divide-rule-subtle bg-canvas">
        {loading && <p className="p-6 text-ink-muted">Loading…</p>}
        {!loading && items.length === 0 && <p className="p-6 text-ink-muted">No items yet.</p>}
        {items.map((it) => (
          <div key={it.id} className="p-5 grid grid-cols-12 gap-4 items-center hover:bg-sand transition-colors" data-testid={`row-${resource.key}-${it.id}`}>
            <div className="col-span-1">
              {(it.image_url) && <img src={it.image_url} alt="" className="w-14 h-14 object-cover" />}
            </div>
            <div className="col-span-7">
              <p className="font-display text-xl text-ink">{it.name || it.title || it.author || it.quote?.slice(0, 60)}</p>
              <p className="text-ink-muted text-sm line-clamp-2 mt-1">{it.description || it.quote || it.source}</p>
            </div>
            <div className="col-span-2 font-mono-label text-ink-muted">
              {it.bed_config || it.category || it.distance || it.icon}
            </div>
            <div className="col-span-2 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => openEdit(it)} className="rounded-none" data-testid={`edit-${it.id}`}>Edit</Button>
              <Button variant="outline" size="sm" onClick={() => del(it)} className="rounded-none text-destructive border-destructive" data-testid={`delete-${it.id}`}>Delete</Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl bg-canvas rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-ink">{editing?.__new ? `New ${resource.label}` : `Edit ${resource.label}`}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 py-4">
              {resource.fields.map((f) => (
                <div key={f.name} className="space-y-1.5">
                  <Label className="font-mono-label text-ink-muted">{f.label}{f.required && <span className="text-ember"> *</span>}</Label>
                  {f.type === "textarea" ? (
                    <Textarea
                      value={editing.data[f.name] ?? ""}
                      onChange={(e) => setEditing({ ...editing, data: { ...editing.data, [f.name]: e.target.value } })}
                      className="rounded-none min-h-[100px]"
                      data-testid={`field-${f.name}`}
                    />
                  ) : f.type === "bool" ? (
                    <label className="inline-flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={!!editing.data[f.name]}
                        onChange={(e) => setEditing({ ...editing, data: { ...editing.data, [f.name]: e.target.checked } })}
                        data-testid={`field-${f.name}`}
                      />
                      <span>Yes</span>
                    </label>
                  ) : (
                    <Input
                      type={f.type === "number" ? "number" : "text"}
                      value={editing.data[f.name] ?? ""}
                      onChange={(e) => setEditing({ ...editing, data: { ...editing.data, [f.name]: e.target.value } })}
                      className="rounded-none"
                      data-testid={`field-${f.name}`}
                    />
                  )}
                  {f.help && <p className="text-xs text-ink-muted">{f.help}</p>}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-none">Cancel</Button>
            <Button onClick={save} className="btn-ember !rounded-none" data-testid="save-resource-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Inquiries({ inquiries, reload }) {
  const del = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await api.delete(`/inquiries/${id}`);
    toast.success("Deleted");
    reload();
  };
  return (
    <div>
      <h2 className="font-display text-3xl text-ink mb-6">Guest inquiries</h2>
      {inquiries.length === 0 ? (
        <p className="text-ink-muted">No inquiries yet.</p>
      ) : (
        <div className="space-y-4">
          {inquiries.map((i) => (
            <div key={i.id} className="bg-sand p-6 border border-rule-subtle" data-testid={`inquiry-${i.id}`}>
              <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="space-y-1">
                  <p className="font-display text-2xl text-ink">{i.name}</p>
                  <p className="font-mono-label text-ink-muted">
                    {i.email} · {i.phone || "no phone"} · {i.guests} guest{i.guests !== 1 ? "s" : ""}
                  </p>
                  {(i.check_in || i.check_out) && (
                    <p className="font-mono-label text-ember">
                      {i.check_in || "?"} → {i.check_out || "?"}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <p className="font-mono-label text-ink-muted">{new Date(i.created_at).toLocaleString()}</p>
                  <Button variant="outline" size="sm" onClick={() => del(i.id)} className="rounded-none text-destructive border-destructive">Delete</Button>
                </div>
              </div>
              <p className="mt-4 text-ink-soft leading-relaxed border-t border-rule-subtle pt-4">{i.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsPanel() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/admin").then((r) => setS(r.data)).catch(() => {});
  }, []);

  const update = (k) => (e) => setS({ ...s, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      const { id, ...payload } = s; // eslint-disable-line
      await api.put("/settings", payload);
      toast.success("Settings saved");
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setBusy(false);
    }
  };

  if (!s) return <p className="text-ink-muted">Loading settings…</p>;

  const sections = [
    {
      label: "Google Business Profile",
      hint: "Drives the rating block on Home, Contact and FAQ pages. Optionally enable live review fetch by adding a Google Maps API key with Places API enabled.",
      fields: [
        ["google_rating", "Star rating (e.g. 4.6)", "number"],
        ["google_review_count", "Total review count", "number"],
        ["google_business_url", "Google Business profile / Maps URL", "text"],
        ["google_place_id", "Google Place ID (optional, for live fetch)", "text"],
        ["google_maps_api_key", "Google Maps API key (optional, for live fetch)", "text"],
      ],
    },
    {
      label: "Tracking / Analytics",
      hint: "Paste your GA4 measurement ID (looks like G-XXXXXXX) to enable Google Analytics on top of the built-in analytics. Optional Google Tag Manager container ID also supported.",
      fields: [
        ["ga4_measurement_id", "GA4 Measurement ID (G-XXXXXXX)", "text"],
        ["google_tag_manager_id", "Google Tag Manager Container ID (GTM-XXXXXXX) — optional", "text"],
      ],
    },
    {
      label: "Search Engine Verifications",
      hint: "Paste the meta-tag verification CONTENT VALUE only (not the full <meta> tag). Found in: Google Search Console → Settings → Ownership verification → HTML tag · Bing Webmaster Tools → Site verification → HTML tag · Facebook Business Manager → Brand Safety → Domains.",
      fields: [
        ["google_site_verification", "Google Search Console (google-site-verification content)", "text"],
        ["bing_site_verification", "Bing Webmaster (msvalidate.01 content)", "text"],
        ["facebook_domain_verification", "Facebook domain verification content", "text"],
        ["pinterest_verification", "Pinterest verification content", "text"],
        ["yandex_verification", "Yandex verification content (optional)", "text"],
      ],
    },
    {
      label: "Citations & Local SEO Directories",
      hint: "Paste the full URLs to your listings on each directory. Empty fields are hidden from the public site. NAP consistency across these directories is the #1 local SEO ranking factor.",
      fields: [
        ["tripadvisor_url", "Tripadvisor URL", "text"],
        ["yelp_url", "Yelp URL", "text"],
        ["facebook_url", "Facebook Page URL", "text"],
        ["instagram_url", "Instagram URL", "text"],
        ["twitter_url", "X / Twitter URL", "text"],
        ["linkedin_url", "LinkedIn URL", "text"],
        ["youtube_url", "YouTube channel URL", "text"],
        ["apple_maps_url", "Apple Maps URL", "text"],
        ["bing_places_url", "Bing Places URL", "text"],
        ["bbb_url", "Better Business Bureau URL", "text"],
      ],
    },
    {
      label: "NAP (Name · Address · Phone)",
      hint: "Your canonical name, address and phone — must appear identically on every citation site to maximize local SEO trust.",
      fields: [
        ["nap_name", "Business name", "text"],
        ["nap_street", "Street address", "text"],
        ["nap_city", "City", "text"],
        ["nap_region", "State (2-letter)", "text"],
        ["nap_postal", "Postal code", "text"],
        ["nap_phone", "Phone (display format)", "text"],
      ],
    },
  ];

  return (
    <div data-testid="settings-panel">
      <div className="flex items-start justify-between gap-6 flex-wrap mb-8">
        <div>
          <h2 className="font-display text-3xl text-ink">Local SEO &amp; site settings</h2>
          <p className="text-ink-muted mt-2 max-w-2xl">
            One place to maintain everything Google, Bing and ChatGPT use to rank and cite your property.
          </p>
        </div>
        <Button onClick={save} disabled={busy} className="btn-ember !rounded-none" data-testid="settings-save-btn">
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </div>

      {/* NAP preview banner */}
      <div className="bg-sand border-l-2 border-ember p-6 mb-6" data-testid="nap-preview">
        <p className="font-mono-label text-ember mb-2">Canonical NAP — copy this exactly to every directory</p>
        <p className="font-display text-2xl text-ink leading-tight">
          {s.nap_name}<br />
          {s.nap_street}, {s.nap_city}, {s.nap_region} {s.nap_postal}<br />
          {s.nap_phone}
        </p>
      </div>

      {/* Sitemap submission helper */}
      <div className="bg-canvas border border-rule-strong p-6 mb-10" data-testid="sitemap-submission">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="max-w-2xl">
            <p className="font-mono-label text-ember mb-2">Search engine submission · do this after launch</p>
            <p className="font-display text-2xl text-ink leading-tight">Submit your sitemap.</p>
            <p className="text-ink-soft mt-3 text-sm leading-relaxed">
              Once verifications are saved above, submit your sitemap URL to Google and Bing so they
              start crawling all 16 blog posts + property pages within a few hours.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="rounded-none"
              onClick={() => { navigator.clipboard.writeText("https://www.westbridgecarrollton.com/sitemap.xml"); toast.success("Sitemap URL copied"); }}
              data-testid="copy-sitemap-btn"
            >
              Copy sitemap URL
            </Button>
            <a href="https://search.google.com/search-console/sitemaps" target="_blank" rel="noopener noreferrer" className="btn-ember" data-testid="submit-google-btn">
              Submit on Google
            </a>
            <a href="https://www.bing.com/webmasters/sitemaps" target="_blank" rel="noopener noreferrer" className="btn-ink" data-testid="submit-bing-btn">
              Submit on Bing
            </a>
          </div>
        </div>
      </div>

      <div className="space-y-12">
        {sections.map((sec) => (
          <div key={sec.label} data-testid={`settings-section-${sec.label.split(" ")[0].toLowerCase()}`}>
            <h3 className="font-display text-2xl text-ink mb-1">{sec.label}</h3>
            <p className="text-ink-muted text-sm mb-6 max-w-3xl">{sec.hint}</p>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
              {sec.fields.map(([key, label, type]) => (
                <div key={key} className="space-y-1.5">
                  <Label className="font-mono-label text-ink-muted">{label}</Label>
                  <Input
                    type={type}
                    value={s[key] ?? ""}
                    onChange={update(key)}
                    className="rounded-none"
                    data-testid={`settings-${key}`}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 flex justify-end">
        <Button onClick={save} disabled={busy} className="btn-ember !rounded-none">
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/analytics/summary").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-ink-muted">Loading analytics…</p>;

  const stats = [
    { key: "page-7d", label: "Page views · 7 days", value: data.views_7d },
    { key: "page-30d", label: "Page views · 30 days", value: data.views_30d },
    { key: "unique", label: "Unique sessions · 7d", value: data.unique_sessions_7d },
    { key: "cta", label: "CTA clicks · 7 days", value: data.clicks_7d },
    { key: "inquiries", label: "Inquiries · 7 days", value: data.inquiries_7d },
  ];

  return (
    <div data-testid="analytics-dashboard">
      <h2 className="font-display text-3xl text-ink mb-6">Visitor analytics</h2>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-rule-strong border border-rule-strong mb-12">
        {stats.map((s) => (
          <div key={s.key} className="bg-canvas p-6" data-testid={`stat-${s.key}`}>
            <p className="font-mono-label text-ink-muted">{s.label}</p>
            <p className="font-display text-5xl text-ember mt-3">{s.value}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <h3 className="font-display text-2xl text-ink mb-4">Top pages (30 days)</h3>
          <ul className="border border-rule-strong divide-y divide-rule-subtle">
            {data.top_pages.length === 0 && <li className="p-4 text-ink-muted">No data yet — visit some pages first.</li>}
            {data.top_pages.map((p) => (
              <li key={p.path} className="p-4 flex justify-between items-center bg-canvas">
                <span className="font-mono text-sm text-ink-soft truncate">{p.path}</span>
                <span className="font-display text-xl text-ember">{p.count}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-display text-2xl text-ink mb-4">Top clicks (30 days)</h3>
          <ul className="border border-rule-strong divide-y divide-rule-subtle">
            {data.top_clicks.length === 0 && <li className="p-4 text-ink-muted">No clicks recorded yet.</li>}
            {data.top_clicks.map((c) => (
              <li key={c.target} className="p-4 flex justify-between items-center bg-canvas">
                <span className="font-mono text-sm text-ink-soft truncate">{c.target}</span>
                <span className="font-display text-xl text-ember">{c.count}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-12">
        <h3 className="font-display text-2xl text-ink mb-4">Recent activity</h3>
        <div className="border border-rule-strong divide-y divide-rule-subtle max-h-[420px] overflow-y-auto">
          {data.recent.slice(0, 30).map((e) => (
            <div key={e.id} className="p-3 px-4 grid grid-cols-12 gap-3 items-center bg-canvas text-sm">
              <span className="col-span-2 font-mono-label text-ember">{e.type}</span>
              <span className="col-span-5 font-mono text-ink-soft truncate">{e.path}</span>
              <span className="col-span-3 font-mono text-ink-muted truncate">{e.target || "—"}</span>
              <span className="col-span-2 font-mono-label text-ink-muted text-right">
                {new Date(e.created_at).toLocaleTimeString()}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
